/**
 * Design-system preview page (P0-T03), mounted at `#/design` by main.ts.
 *
 * Renders every baseline component from tokens.css so later tasks (and other
 * AIs) can verify the visual language in one place:
 *   Button (incl. "Generate −N credits ✦"), tabs, upload box, option chips,
 *   bottom sheet, empty state, toast.
 *
 * Interaction is wired with data-action attributes — no framework, vanilla DOM.
 */

import { bindCompareSliders, compareSliderHtml } from '../components/compareSlider.js';
import { uploadGuideHtml } from '../components/uploadGuide.js';

/** Inline SVG demo images so the slider can be previewed without any asset files. */
function demoImage(kind: 'before' | 'after'): string {
  const warm = kind === 'after';
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 500'><defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='${warm ? '#8b5cf6' : '#4b5563'}'/><stop offset='1' stop-color='${warm ? '#e349f5' : '#1f2937'}'/></linearGradient></defs><rect width='400' height='500' fill='url(#g)'/><circle cx='200' cy='210' r='${warm ? 90 : 84}' fill='${warm ? '#fde68a' : '#9ca3af'}'/><rect x='110' y='320' width='180' height='140' rx='70' fill='${warm ? '#fbcfe8' : '#6b7280'}'/></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

const ACTIONS = {
  OPEN_SHEET: 'open-sheet',
  CLOSE_SHEET: 'close-sheet',
  SHOW_TOAST: 'show-toast',
  SELECT_TAB: 'select-tab',
  SELECT_CHIP: 'select-chip',
} as const;

function section(title: string, body: string): string {
  return `<h2 class="section-title">${title}</h2><div class="stack">${body}</div>`;
}

export function renderDesign(root: HTMLElement): void {
  root.innerHTML = `
  <div class="shell">
    <a href="#/" class="muted" style="text-decoration:none;">← Back</a>
    <h1 class="page-title" style="margin-top:12px;">Design preview</h1>
    <p class="muted">Baseline components · P0-T03 · dark theme, 430px shell</p>

    ${section('Buttons', `
      <button class="btn btn-primary btn-block" data-action="${ACTIONS.SHOW_TOAST}" data-msg="Primary button tapped">
        Try now
      </button>
      <button class="btn btn-secondary btn-block" data-action="${ACTIONS.SHOW_TOAST}" data-msg="Secondary button tapped">
        Explore Now
      </button>
      <button class="btn btn-generate" data-action="${ACTIONS.SHOW_TOAST}" data-msg="Generate tapped — 30 credits would be reserved">
        <span>Generate</span>
        <span class="cost">−30 credits <span class="credit">✦</span></span>
      </button>
      <button class="btn btn-generate" disabled>
        <span>Generate</span>
        <span class="cost">−150 credits <span class="credit">✦</span></span>
      </button>
      <p class="muted">Generate buttons always show the exact ✦ cost and stay disabled until inputs are valid.</p>
    `)}

    ${section('Tabs', `
      <div class="tabs" data-tabs>
        <button class="tab active" data-action="${ACTIONS.SELECT_TAB}">All Effects</button>
        <button class="tab" data-action="${ACTIONS.SELECT_TAB}">Trending</button>
        <button class="tab" data-action="${ACTIONS.SELECT_TAB}">Change clothes</button>
        <button class="tab" data-action="${ACTIONS.SELECT_TAB}">Hair salon</button>
        <button class="tab" data-action="${ACTIONS.SELECT_TAB}">Family</button>
      </div>
    `)}

    ${section('Upload box', `
      <label class="upload-box" tabindex="0">
        <input type="file" accept="image/*" hidden data-upload-input />
        <span class="plus">+</span>
        <span>Upload Image</span>
      </label>
      <p class="muted" data-upload-hint>Use a clear, front-facing photo with only one person for best results.</p>
    `)}

    ${section('Option chips (Hair Style)', `
      <div class="chips" data-chips>
        <button class="chip" data-action="${ACTIONS.SELECT_CHIP}"><span class="thumb">∅</span>None</button>
        <button class="chip selected" data-action="${ACTIONS.SELECT_CHIP}"><span class="thumb">≋</span>Straight</button>
        <button class="chip" data-action="${ACTIONS.SELECT_CHIP}"><span class="thumb">〜</span>Bangs</button>
        <button class="chip" data-action="${ACTIONS.SELECT_CHIP}"><span class="thumb">❋</span>Curly</button>
      </div>
    `)}

    ${section('Bottom sheet', `
      <button class="btn btn-secondary btn-block" data-action="${ACTIONS.OPEN_SHEET}">Open “Perfect Your Upload”</button>
    `)}

    ${section('Before / After slider', `
      ${compareSliderHtml({ before: demoImage('before'), after: demoImage('after'), ratio: '4 / 5' })}
      <p class="muted">Drag, swipe or use ← → keys. Shared component: <code>components/compareSlider.ts</code> (P2-T10).</p>
    `)}

    ${section('Empty state', `
      <div class="empty">
        <div class="empty-icon">◻</div>
        <div><strong>Nothing here yet.</strong><br /><span class="muted">Your AI creations will appear here. Start generating now <span class="credit">✦</span></span></div>
        <button class="btn btn-primary" data-action="${ACTIONS.SHOW_TOAST}" data-msg="Explore Now tapped">Explore Now</button>
      </div>
    `)}

    ${section('Toast', `
      <button class="btn btn-secondary btn-block" data-action="${ACTIONS.SHOW_TOAST}" data-msg="Demo only — no AI runs here yet">Show toast</button>
    `)}

    <div class="divider"></div>
    <p class="muted">Loomia · P0 scaffold · tokens in <code>src/styles/tokens.css</code></p>
  </div>

  <div class="sheet-backdrop" data-sheet>
    ${uploadGuideHtml('face').replace('data-ug-ok', `data-action="${ACTIONS.CLOSE_SHEET}"`)}
  </div>

  <div class="toast" data-toast role="status"></div>
  `;

  wireInteractions(root);
  bindCompareSliders(root);
}

function wireInteractions(root: HTMLElement): void {
  const toast = root.querySelector<HTMLElement>('[data-toast]');
  const sheet = root.querySelector<HTMLElement>('[data-sheet]');
  let toastTimer: number | undefined;

  const showToast = (msg: string): void => {
    if (!toast) return;
    toast.textContent = msg;
    toast.classList.add('show');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('show'), 2200);
  };

  root.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
    if (!target) {
      // Tapping the backdrop (outside the sheet) dismisses it.
      if ((e.target as HTMLElement).hasAttribute?.('data-sheet')) {
        sheet?.classList.remove('open');
      }
      return;
    }

    switch (target.dataset.action) {
      case ACTIONS.OPEN_SHEET:
        sheet?.classList.add('open');
        break;
      case ACTIONS.CLOSE_SHEET:
        sheet?.classList.remove('open');
        break;
      case ACTIONS.SHOW_TOAST:
        showToast(target.dataset.msg ?? 'Done');
        break;
      case ACTIONS.SELECT_TAB: {
        target.parentElement
          ?.querySelectorAll('.tab')
          .forEach((t) => t.classList.remove('active'));
        target.classList.add('active');
        break;
      }
      case ACTIONS.SELECT_CHIP: {
        // Single-select within its chip group (matches editor behavior).
        target.parentElement
          ?.querySelectorAll('.chip')
          .forEach((c) => c.classList.remove('selected'));
        target.classList.add('selected');
        break;
      }
    }
  });

  // Upload box: reflect the chosen file name in the hint line.
  const uploadInput = root.querySelector<HTMLInputElement>('[data-upload-input]');
  const uploadHint = root.querySelector<HTMLElement>('[data-upload-hint]');
  uploadInput?.addEventListener('change', () => {
    const file = uploadInput.files?.[0];
    if (uploadHint) {
      uploadHint.textContent = file
        ? `Selected: ${file.name} — ready to generate.`
        : 'Use a clear, front-facing photo with only one person for best results.';
    }
  });

  // Escape closes the sheet.
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') sheet?.classList.remove('open');
  });
}
